import { createContext } from 'react';
import { User, PromoResult } from '@/lib/api';

/**
 * Why a Google sign-in did not finish. The first group comes from the
 * browser side (lib/google-signin.ts GoogleSignInError; 'tap_again' is a
 * popup blocked because the SDK was still loading at the tap), the second
 * from POST /api/auth/google, and 'network' covers both.
 */
export type GoogleLoginCode =
  | 'cancelled'
  | 'popup_blocked'
  | 'tap_again'
  | 'unauthorized_domain'
  | 'network'
  | 'unavailable'
  | 'account_exists'
  | 'unknown'
  | 'missing_token'
  | 'invalid_google_token'
  | 'unsupported_provider'
  | 'google_email_unverified'
  | 'account_merged'
  | 'google_signin_disabled'
  | 'google_signin_failed';

export interface GoogleLoginResult {
  success: boolean;
  error?: string;
  code?: GoogleLoginCode | string;
  isNewUser?: boolean;
}

export interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (email: string, password: string, name?: string, promoCode?: string) => Promise<{ success: boolean; error?: string; promo?: PromoResult | null; verificationEmailSent?: boolean }>;
  /**
   * "Continue with Google". Call it straight from the click handler: its
   * first statement opens the Google popup, which browsers only allow inside
   * the tap.
   */
  loginWithGoogle: () => Promise<GoogleLoginResult>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);
